import * as S from './styles'
import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { Badge } from 'antd'
import { AiFillSchedule } from "react-icons/ai"
import {
  BsFillGrid1X2Fill,
  BsFileEarmarkTextFill,
  BsBoxArrowRight,
  BsFileEarmarkRichtext,
  BsFileEarmarkFill
} from 'react-icons/bs'
import { FaUsers, FaBalanceScale, FaBuilding, FaUserCircle } from "react-icons/fa"
import { RiMoneyDollarCircleLine } from 'react-icons/ri'
import { MdAdminPanelSettings, MdSettings, MdInsights, MdSchool } from "react-icons/md"
import { Tooltip } from 'react-tooltip'
import 'react-tooltip/dist/react-tooltip.css'
import { getUserCachedData, updateUserCachedData } from 'utils/UserAuthorization'
import { getEffectiveMenuItems, getUserRoleValue, hasGeneralAccess } from 'utils/rolePermissions'
import { useNotifications } from 'contexts/NotificationContext'
import colaborador from "../../../services/collaborator/me"
import { preloadRoute } from 'components/Router/routeLoaders'

const SIDEBAR_TOOLTIP_STYLE = {
  zIndex: 10000,
  backgroundColor: '#1f2937',
  color: '#fff',
  fontSize: 13,
  padding: '6px 10px',
  borderRadius: 6,
  maxWidth: 260,
}

function Menu({ setLogout, isMobile, mobileMenuOpen, setMobileMenuOpen, aberto, onAlternar }) {
  const menuRef = useRef(null)
  const indicadorRef = useRef(null)
  // aberto/fechado vem do layout (botão ao lado do logo); sem ele, controla sozinho
  const [menuLocal, setMenuLocal] = useState(false)
  const { pathname } = useLocation()
  const menu = aberto ?? menuLocal

  // Leva a faixa dourada até o item ativo
  useLayoutEffect(() => {
    const mover = () => {
      const caixa = menuRef.current
      const faixa = indicadorRef.current
      if (!caixa || !faixa) return
      const ativo = caixa.querySelector('a.active')
      if (!ativo) { faixa.style.opacity = '0'; return }
      const r = ativo.getBoundingClientRect()
      const c = caixa.getBoundingClientRect()
      faixa.style.opacity = '1'
      faixa.style.height = `${r.height - 16}px`
      faixa.style.transform = `translateY(${r.top - c.top + caixa.scrollTop + 8}px)`
    }
    mover()
    const quadro = requestAnimationFrame(mover)
    return () => cancelAnimationFrame(quadro)
  })
  const setMenu = onAlternar || setMenuLocal
  // começa do que já está salvo (login); /me atualiza em seguida
  const [userData, setUserData] = useState(() => getUserCachedData() || {});

  const { hasUnreadPublications, publicationUnreadCount, clearPublicationBadge } = useNotifications()
  useEffect(() => {
    let mounted = true
    colaborador().then((user) => {
      if (mounted) setUserData(user.data)
      // Mantém o cache local (usado por Router/index.jsx) sincronizado com o
      // que o backend calculou, sem precisar de um novo login.
      updateUserCachedData(user.data)
    })
    return () => { mounted = false }
  }, [])
  const effectiveMenuItems = getEffectiveMenuItems(userData)
  const hasMenuItem = (key) => effectiveMenuItems.includes(key)
  const publicationBadgeStyle = {
    minWidth: 20,
    height: 20,
    lineHeight: '20px',
    borderRadius: 10,
    padding: 0,
    fontSize: 12,
    fontWeight: 700,
    color: 'white',
    border: 'none',
    boxShadow: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }

  
  useEffect(() => {
    const container = document.querySelector('[data-container="main"]')
    if (container && !isMobile) {
      container.style.marginLeft = menu ? '240px' : '65px'
    } else if (container && isMobile) {
      container.style.marginLeft = '0'
    }
  }, [menu, isMobile])

  const menuStyle = isMobile 
    ? { 
        position: 'fixed',
        left: mobileMenuOpen ? '0' : '-280px',
        top: '64px',
        height: 'calc(100vh - 64px)',
        width: '280px',
        zIndex: 999,
        transition: 'left 0.3s ease'
      }
    : !menu 
      ? { width: '65px' } 
      : { width: '240px' }
  
  const menuItemTextStyle = isMobile ? {} : menu ? {} : { display: 'none' }

  const menuItems = [
    ...(hasMenuItem('plataforma-admin') ? [{ to: '/plataforma-admin', icon: MdAdminPanelSettings, label: 'Administração Global' }] : []),
    ...(hasMenuItem('dashboard') ? [{ to: '/dashboard', icon: BsFillGrid1X2Fill, label: 'Meu Painel' }] : []),
    ...(hasMenuItem('schedule') ? [{ to: '/schedule', icon: AiFillSchedule, label: 'Agenda' }] : []),
    ...(hasMenuItem('legal-kanban') ? [{ to: '/legal-kanban', icon: FaBalanceScale, label: 'Atividades' }] : []),
    ...(hasMenuItem('publications') ? [{ to: '/publications', icon: BsFileEarmarkFill, label: 'Publicações' }] : []),
    ...(hasMenuItem('active-processes') ? [{ to: '/active-processes', icon: BsFileEarmarkTextFill, label: 'Processos', ativoEm: ['/concluded-processes', '/consulta-envolvido'] }] : []),
    ...(hasMenuItem('clients') ? [{ to: '/clients', icon: FaUsers, label: 'Contatos' }] : []),
    ...(hasMenuItem('financeiro') ? [{ to: '/financeiro', icon: RiMoneyDollarCircleLine, label: 'Financeiro' }] : []),
    ...(hasMenuItem('document-templates') ? [{ to: '/document-templates', icon: BsFileEarmarkRichtext, label: 'Modelos' }] : []),
    ...(hasGeneralAccess(getUserRoleValue(userData)) ? [{ to: '/business-intelligence', icon: MdInsights, label: 'Business Intelligence' }] : []),
      // Auditoria está temporariamente oculta na UI pois o sistema de logs não está disponível.
      // {(canSeeAudit ? [{ to: '/auditoria', icon: FaHistory, label: 'Auditoria' }] : [])},
  ]

  return (
    <>
      {isMobile && mobileMenuOpen && (
        <S.MobileOverlay onClick={() => setMobileMenuOpen(false)} />
      )}
      
      <S.Menu ref={menuRef} style={menuStyle}>
        <S.IndicadorMenu aria-hidden ref={indicadorRef} />
        {/* no celular o botão de menu fica no topo */}
        {isMobile || onAlternar ? null : (
          // No desktop sem o botão do topo, usa o estado menu
          menu ? (
            <S.MenuButtonLeft onClick={() => setMenu(!menu)} />
          ) : (
            <S.MenuButtonRight onClick={() => setMenu(!menu)} />
          )
        )}
        <S.MenuItems>
          {menuItems.map((item, index) => {
            const isPublications = item.to === '/publications'
            const IconComponent = item.icon
            const linkClass = (!menu && !isMobile) ? 'only-icon' : ''
            return (
              <div key={index}>
                <S.MenuLink 
                  to={item.to} 
                  data-tooltip-id={!menu && !isMobile ? `tooltip-${index}` : undefined}
                  data-tooltip-content={!menu && !isMobile ? item.label : undefined}
                  className={({ isActive }) => [linkClass, isActive || (item.ativoEm || []).some((r) => pathname.startsWith(r)) ? 'active' : ''].filter(Boolean).join(' ')}
                  style={{ position: 'relative' }}
                  onPointerEnter={() => preloadRoute(item.to)}
                  onFocus={() => preloadRoute(item.to)}
                  onClick={() => {
                    if (isMobile) setMobileMenuOpen(false)
                    if (item.to === '/publications') {
                      try { clearPublicationBadge() } catch (err) {}
                    }
                  }}
                >
                  {isPublications && hasUnreadPublications ? (
                    <Badge
                      className='selo-menu'
                      count={publicationUnreadCount > 9 ? '9+' : publicationUnreadCount}
                      overflowCount={9}
                      color='#ef4444'
                      offset={[2, 1]}
                      style={publicationBadgeStyle}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, flexShrink: 0 }}>
                        <IconComponent size={20} />
                      </span>
                    </Badge>
                  ) : (
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, flexShrink: 0 }}>
                      <IconComponent size={20} />
                    </span>
                  )}
                  <p style={menuItemTextStyle}>{item.label}</p>
                  {item.badge && (
                    <span style={{
                      position: 'absolute',
                      right: (menu || isMobile) ? '20px' : '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: '#13143A',
                      color: 'white',
                      borderRadius: '10px',
                      padding: '2px 6px',
                      fontSize: 12,
                      fontWeight: 'bold',
                      minWidth: '18px',
                      textAlign: 'center',
                      display: (menu || isMobile) ? 'block' : 'none'
                    }}>
                      {item.badge}
                    </span>
                  )}
                </S.MenuLink>
              </div>
            )
          })}

  
         
          <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '8px 16px' }} />
          <div>
            <S.MenuLink
              to='/yrm-academy'
              className={(!menu && !isMobile) ? 'only-icon' : ''}
              data-tooltip-id={!menu && !isMobile ? 'tooltip-academy' : undefined}
              data-tooltip-content={!menu && !isMobile ? 'YRM Academy' : undefined}
              onPointerEnter={() => preloadRoute('/yrm-academy')}
              onClick={() => isMobile && setMobileMenuOpen(false)}
            >
              <MdSchool size={20} />
              <p style={menuItemTextStyle}>YRM Academy</p>
            </S.MenuLink>
          </div>
          <div>
            <S.MenuLink 
              to='/settings' 
              className={(!menu && !isMobile) ? 'only-icon' : ''}
              data-tooltip-id={!menu && !isMobile ? 'tooltip-settings' : undefined}
              data-tooltip-content={!menu && !isMobile ? 'Configurações' : undefined}
              onPointerEnter={() => preloadRoute('/settings')}
              onFocus={() => preloadRoute('/settings')}
              onClick={() => isMobile && setMobileMenuOpen(false)}
            >
              <MdSettings size={20} />
              <p style={menuItemTextStyle}>Configurações</p>
            </S.MenuLink>
          </div>
          {hasMenuItem('company') && (
            <div>
              <S.MenuLink
                to='/company'
                className={(!menu && !isMobile) ? 'only-icon' : ''}
                data-tooltip-id={!menu && !isMobile ? 'tooltip-company-link' : undefined}
                data-tooltip-content={!menu && !isMobile ? 'Conta e assinatura' : undefined}
                onPointerEnter={() => preloadRoute('/company')}
                onClick={() => isMobile && setMobileMenuOpen(false)}
              >
                <FaBuilding size={20} />
                <p style={menuItemTextStyle}>Conta e assinatura</p>
              </S.MenuLink>
            </div>
          )}
          <div>
            <S.MenuLink
              to='/perfil'
              className={(!menu && !isMobile) ? 'only-icon' : ''}
              data-tooltip-id={!menu && !isMobile ? 'tooltip-perfil' : undefined}
              data-tooltip-content={!menu && !isMobile ? 'Meu perfil' : undefined}
              onPointerEnter={() => preloadRoute('/perfil')}
              onClick={() => isMobile && setMobileMenuOpen(false)}
            >
              <FaUserCircle size={20} />
              <p style={menuItemTextStyle}>Meu perfil</p>
            </S.MenuLink>
          </div>

          {/* Company info at the end of the menu */}
          <div style={{ marginTop: 12 }}>
            {(() => {
              const companyName = getUserCachedData()?.company?.nome || getUserCachedData()?.company?.name || ''
              return (
                <div
                  data-tooltip-id={!menu && !isMobile ? 'tooltip-company' : undefined}
                  data-tooltip-content={!menu && !isMobile ? (companyName || 'Empresa') : undefined}
                  style={{ padding: '8px 20px', color: 'rgba(255,255,255,0.45)', fontSize: 12 }}
                >
                  <strong style={menuItemTextStyle}>{companyName || 'Empresa'}</strong>
                </div>
              )
            })()}
          </div>

         
          <div>
            <S.MenuLink 
              $isLogout 
              className={(!menu && !isMobile) ? 'only-icon' : ''}
              onClick={() => {
                setLogout(true)
                isMobile && setMobileMenuOpen(false)
              }} 
              data-tooltip-id={!menu && !isMobile ? 'tooltip-logout' : undefined}
              data-tooltip-content={!menu && !isMobile ? 'Sair' : undefined}
            >
              <BsBoxArrowRight size={20} />
              <p style={menuItemTextStyle}>Sair</p>
            </S.MenuLink>
          </div>
        </S.MenuItems>
      </S.Menu>

      {/* Tooltips globais fora do container do menu (um por item) */}
      {!menu && !isMobile && (
        <>
          {[...menuItems.map((_, index) => `tooltip-${index}`), 'tooltip-academy', 'tooltip-settings', 'tooltip-company-link', 'tooltip-perfil', 'tooltip-company', 'tooltip-logout'].map((id) => (
            <Tooltip key={id} id={id} place="right" style={SIDEBAR_TOOLTIP_STYLE} offset={12} />
          ))}
        </>
      )}
    </>
  )
}

export default Menu
